import React, { useRef, useState, useEffect } from 'react';
import { Row, Col, Card, Avatar, Typography, Divider, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { Domain, EntityType, RecommendationModule as RecommendationModuleType } from '../../types.generated';
import { ReactComponent as IconDataDomain } from '../../images/icon-data-domain.svg';
import { ReactComponent as IconDataEntity } from '../../images/icon-data-entity.svg';
import { ReactComponent as IconDataProduct } from '../../images/icon-data-product.svg';
import { useEntityRegistry } from '../useEntityRegistry';

// Using TypeScript interface for prop validation instead of PropTypes
interface DomainCardsProps {
    domainRecommendationModule: RecommendationModuleType;
}

const TruncatedTitle = ({ title, domain }: { title: string; domain: Domain }) => {
    const titleRef = useRef<HTMLHeadingElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const entityRegistry = useEntityRegistry();

    useEffect(() => {
        const checkTruncation = () => {
            if (titleRef.current) {
                const element = titleRef.current;
                // Check if the content is wider than the container
                setIsTruncated(element.scrollWidth > element.clientWidth);
            }
        };

        checkTruncation();
        window.addEventListener('resize', checkTruncation);

        return () => {
            window.removeEventListener('resize', checkTruncation);
        };
    }, [title]);

    const titleElement = (
        <Typography.Title
            ref={titleRef}
            level={5}
            className="f-text-medium-content f-text-single-line f-text-semibold f-color-dark-black-s80 mb-2"
            ellipsis
        >
            <Link to={entityRegistry.getEntityUrl(EntityType.Domain, domain.urn)} style={{ color: 'inherit' }}>
                {title}
            </Link>
        </Typography.Title>
    );

    return isTruncated ? <Tooltip title={title}>{titleElement}</Tooltip> : titleElement;
};

export const DomainCards = ({ domainRecommendationModule }: DomainCardsProps) => {
    const entityRegistry = useEntityRegistry();

    return (
        <Row align="middle" gutter={[16, 16]}>
            {domainRecommendationModule?.content?.slice(0, 8).map((domainContent) => {
                const domain = domainContent.entity as Domain;
                if (!domain) return null;

                const entityCount = domainContent.params?.contentParams?.count || 0;
                const dataProductCount = domain.entities?.total || 0;
                const domainName = domain.properties?.name || domain.id || domain.urn;
                const domainUrl = entityRegistry.getEntityUrl(EntityType.Domain, domain.urn);

                return (
                    <Col key={domain.urn} xs={24} lg={8} xl={6}>
                        <Card className="f-domain-card pa-3">
                            <Row wrap={false} align="middle" className="f-gap-3">
                                <Col flex="none">
                                    <Link to={domainUrl}>
                                        <Avatar
                                            icon={<IconDataDomain />}
                                            className="f-avatar-default f-avatar-light-blue"
                                        />
                                    </Link>
                                </Col>
                                <Col flex="auto">
                                    <TruncatedTitle title={domainName} domain={domain} />
                                    <Row className="f-gap-2" align="middle">
                                        <Col flex="none">
                                            <Row className="f-gap-1" align="middle">
                                                <Col flex="none">
                                                    <IconDataEntity className="f-icon-size-xs f-color-dark-black-s50" />
                                                </Col>
                                                <Col flex="none">
                                                    <Typography.Text className="f-text-medium-content f-text-single-line f-color-dark-black-s50">
                                                        {entityCount} entities
                                                    </Typography.Text>
                                                </Col>
                                            </Row>
                                        </Col>
                                        <Divider type="vertical" />
                                        <Col flex="none">
                                            <Row className="f-gap-1" align="middle">
                                                <Col flex="none">
                                                    <IconDataProduct className="f-icon-size-xs f-color-dark-black-s50" />
                                                </Col>
                                                <Col flex="none">
                                                    <Typography.Text className="f-text-medium-content f-text-single-line f-color-dark-black-s50">
                                                        {dataProductCount} data products
                                                    </Typography.Text>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                );
            })}
        </Row>
    );
};
